import type { AxiosResponse } from "axios";
import { isGmailContext } from "./api";

/** Axios `response.headers` (union of `RawAxiosResponseHeaders` and `AxiosResponseHeaders`). */
export type AxiosResponseHeadersParam = AxiosResponse["headers"];

const StorageKey = "salescrm";
/** Used to restore admin session when exiting "Login as" (impersonation). */
export const ImpersonationReturnKey = "salescrm_impersonation_return";

export async function currentSession(): Promise<any> {
  const value = localStorage.getItem(StorageKey);
  const val = value ? JSON.parse(value) : {};
  return Promise.resolve(val);
}

export function loginWithJwt(jwt) {
  localStorage.setItem(StorageKey, JSON.stringify({ jwt }));
}

/** Lowercase; Axios normalizes response header names. Server sends `X-Refreshed-JWT`. */
const REFRESHED_JWT_HEADER = "x-refreshed-jwt";

function refreshedJwtFromHeaders(headers: AxiosResponseHeadersParam): string | undefined {
  const record = headers as Record<string, unknown>;
  const refreshedJwtToken = (record[REFRESHED_JWT_HEADER] ||
    record["X-Refreshed-JWT"] ||
    "") as string;
  return refreshedJwtToken || undefined;
}

/** Merge a server-refreshed JWT into the existing session (e.g. from `X-Refreshed-JWT` on `GET api/users/me`). */
function mergeRefreshedJwtIntoSession(jwt: string): void {
  if (isGmailContext()) {
    try {
      const email = window.inboxSdk.User.getEmailAddress();
      chrome.storage.local.set({ [email]: { token: jwt } });
    } catch {}
  } else {
    const raw = localStorage.getItem(StorageKey);
    const session = raw ? JSON.parse(raw) : {};
    localStorage.setItem(StorageKey, JSON.stringify({ ...session, jwt }));
  }
}

/** Apply `X-Refreshed-JWT` from Axios `response.headers` to local session storage. */
export function mergeRefreshedJwtFromResponseHeaders(headers: AxiosResponseHeadersParam): void {
  const token = refreshedJwtFromHeaders(headers);
  if (token) mergeRefreshedJwtIntoSession(token);
}

/**
 * JWT to persist or pass to the host after `GET .../users/me` (e.g. Chrome OAuth callback must use refreshed token in
 * `postMessage`, not only the URL param).
 */
export function jwtAfterUsersMeResponse(
  headers: AxiosResponseHeadersParam,
  urlOrRequestJwt: string
): string {
  return refreshedJwtFromHeaders(headers) ?? urlOrRequestJwt;
}

export function logout() {
  localStorage.removeItem(StorageKey);
}

/** Save current session JWT so we can restore it when exiting impersonation. Call before redirecting to main app with impersonation JWT. */
export async function saveImpersonationReturnToken(): Promise<void> {
  const session = await currentSession();
  const jwt = session?.jwt;
  if (jwt) {
    localStorage.setItem(ImpersonationReturnKey, JSON.stringify({ jwt }));
  }
}

export function getImpersonationReturnToken(): { jwt: string } | null {
  const raw = localStorage.getItem(ImpersonationReturnKey);
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    return data?.jwt ? { jwt: data.jwt } : null;
  } catch {
    return null;
  }
}

export function clearImpersonationReturnToken(): void {
  localStorage.removeItem(ImpersonationReturnKey);
}

export function hasImpersonationReturnToken(): boolean {
  return getImpersonationReturnToken() != null;
}
